import { functionalUpdate } from '@tanstack/react-table';
import {
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';

import { isFractionWidth } from './Layout.js';
import { useControllableState } from '../../../core/hooks/useControllableState.js';
import { useDataTableV2ConfigElement } from '../../hooks/useDataTableV2Config.js';
import type {
  Column,
  Header,
  RowData,
  Table,
  TableFeature,
  TableOptions,
} from '../hooks/useTable/types.js';

/**
 * DataTableV2 columnSizing state represented with a columnId key and a pixel size value.
 * @public
 */
export type DataTableV2ColumnSizingState = Record<string, number>;

/**
 * @public
 */
export interface DataTableV2ColumnSizingBaseProps {
  /**
   * Enables resizable columns for the table.
   * @defaultValue false
   */
  resizable?: boolean;

  /**
   * Callback that triggers when a user finishes resizing a column.
   */
  onColumnSizingChange?: (newSizes: DataTableV2ColumnSizingState) => void;
}
/**
 * @public
 */
export interface DataTableV2ColumnSizingControlledProps
  extends DataTableV2ColumnSizingBaseProps {
  /**
   * ColumnSizing state that lets you set the individual column sizes in a controlled scenario.
   */
  columnSizing?: DataTableV2ColumnSizingState;
  /**
   * ColumnSizing state that lets you set the individual column sizes in a uncontrolled scenario.
   */
  defaultColumnSizing?: never;
}
/**
 * @public
 */
export interface DataTableV2ColumnSizingUncontrolledProps
  extends DataTableV2ColumnSizingBaseProps {
  /**
   * ColumnSizing state that lets you set the individual column sizes in a controlled scenario.
   */
  columnSizing?: never;
  /**
   * ColumnSizing state that lets you set the individual column sizes in a uncontrolled scenario.
   */
  defaultColumnSizing?: DataTableV2ColumnSizingState;
}

/**
 * DataTableV2 props associated with columnSizing / columnResizing feature.
 * @public
 */
export type DataTableV2ColumnSizingProps =
  | DataTableV2ColumnSizingControlledProps
  | DataTableV2ColumnSizingUncontrolledProps;

/**
 * @internal
 */
export interface DataTableV2ColumnSizingTableState {
  columnSizing: DataTableV2ColumnSizingState;
  columnSizingInfo: DataTableV2ColumnSizingInfoState;
}

/** @internal */
export interface DataTableV2ColumnSizingHeader {
  getResizeHandler: () => (
    event:
      | ReactMouseEvent<HTMLButtonElement>
      | ReactTouchEvent<HTMLButtonElement>,
  ) => void;

  getResizeExpandHandler: () => (
    event: ReactMouseEvent<HTMLButtonElement>,
  ) => void;
}

/**
 * @internal
 */
export interface DataTableV2ColumnSizingInfoState {
  columnSizingStart: [string, number][];
  deltaOffset: null | number;
  deltaPercentage: null | number;
  isResizingColumn: false | string;
  startOffset: null | number;
  startSize: null | number;
}

/**
 * Extension interface to extend the types of the `HeaderCell` from the tanstack table.
 * @internal
 */
export interface DataTableV2ColumnSizingHeader {
  getKeyboardResizeHandler: () => (
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => void;
}

const getClientX = (event: MouseEvent | TouchEvent): number | undefined => {
  if ('touches' in event && event.touches.length > 0) {
    return event.touches[0].clientX;
  } else if ('changedTouches' in event && event.changedTouches.length > 0) {
    return event.changedTouches[0].clientX;
  } else if ('clientX' in event) {
    return event.clientX;
  }
};

/**
 * @internal
 */
export const DataTableV2ColumnSizing: TableFeature = {
  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    // helper function to clear the focus on the resizer.
    const clearResizerFocus = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    header.getResizeHandler = () => {
      const column = table.getColumn(header.column.id);
      const canResize = column?.getCanResize();

      return (
        event:
          | ReactMouseEvent<HTMLButtonElement>
          | ReactTouchEvent<HTMLButtonElement>,
      ) => {
        if (!column || !canResize) {
          return;
        }

        event.persist();

        // If a double click appears, do not continue further with the single click
        // drag handling
        if (event.detail === 2) {
          return;
        }

        const target = event.target;
        if (target instanceof HTMLElement === false) {
          return;
        }

        const container: HTMLElement | null = target.closest(
          '.strato-table-virtualization-container',
        );
        const startSize =
          target.previousElementSibling?.getBoundingClientRect().width;

        // If the header could not be measured, we can't resize it
        if (startSize === undefined) {
          return;
        }

        const columnSizingStart: [string, number][] = [[column.id, startSize]];

        // Get the starting clientX where the drag started.
        const clientX = getClientX(event.nativeEvent) || null;

        // Keep a reference for columns affected by the sizing.
        const newColumnSizing: DataTableV2ColumnSizingState = {};

        const { minWidth = 30, maxWidth = Number.MAX_SAFE_INTEGER } =
          column.columnDef;
        const size = `${Math.min(Math.max(startSize, minWidth), maxWidth)}px`;
        container?.style.setProperty('--strato-resizing-size', size);
        container?.style.setProperty('pointer-events', 'none');

        // Updating the actual offset in the columnSizingInfo
        const updateOffset = (
          eventType: 'move' | 'end',
          clientXPos?: number,
        ) => {
          if (typeof clientXPos !== 'number') {
            return;
          }

          const old = table.getState().columnSizingInfo;

          const deltaOffset = clientXPos - (old?.startOffset ?? 0);
          // Calculate the percentage so the other columns next to it can be scaled
          // by the same fraction.
          const deltaPercentage = Math.max(
            deltaOffset / (old?.startSize ?? 0),
            -0.9999999,
          );

          columnSizingStart.forEach(([columnId, headerSize]) => {
            newColumnSizing[columnId] =
              Math.round(
                Math.max(headerSize + headerSize * deltaPercentage, 0) * 100,
              ) / 100;
          });
          const { minWidth = 30, maxWidth = Number.MAX_SAFE_INTEGER } =
            column.columnDef;
          const size = `${Math.min(
            Math.max(startSize + deltaOffset, minWidth),
            maxWidth,
          )}px`;

          // While dragging set the custom property value
          container?.style.setProperty('--strato-resizing-size', size);

          // When dragging ends, we can update the actual widths of the columns
          if (eventType === 'end') {
            table.setColumnSizing((old) => ({
              ...old,
              ...newColumnSizing,
            }));
          }
        };

        const onEnd = (clientXPos?: number) => {
          updateOffset('end', clientXPos);

          // Clean up the container styles that have been applied while resizing.
          container?.style.removeProperty('pointer-events');
          // Wait an animation frame to remove the custom property to prevent
          // a layout jump
          requestAnimationFrame(() => {
            container?.style.removeProperty('--strato-resizing-size');
          });

          // If we are coming from mouse movements, then clear the focus on resizer
          clearResizerFocus();

          table.setColumnSizingInfo((old) => ({
            ...old,
            isResizingColumn: false,
            startOffset: null,
            startSize: null,
            deltaOffset: null,
            deltaPercentage: null,
            columnSizingStart: [],
          }));
        };

        const moveHandler = (moveEvent: MouseEvent | TouchEvent) => {
          const clientXPos = getClientX(moveEvent);
          updateOffset('move', clientXPos);
        };

        const upHandler = (upEvent: MouseEvent | TouchEvent) => {
          document.removeEventListener('mousemove', moveHandler);
          document.removeEventListener('mouseup', upHandler);
          document.removeEventListener('touchmove', moveHandler);
          document.removeEventListener('touchend', upHandler);
          const clientXPos = getClientX(upEvent);
          onEnd(clientXPos);
        };

        // TODO: SSR check if document is there.
        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', upHandler);
        document.addEventListener('touchmove', moveHandler);
        document.addEventListener('touchend', upHandler);

        // If the table has any fractional templates, we need to make sure to lock their size in place, before starting
        // to resize the remaining things.
        if (table._getHasFractionalTemplate()) {
          const fractionColumns = table
            .getVisibleLeafColumnsWithoutBuiltin()
            .filter(
              ({ id, columnDef }) =>
                (columnDef.width === undefined ||
                  isFractionWidth(columnDef.width)) &&
                id !== column.id,
            );

          table.setColumnSizing((old) => {
            const newColumnSizing = { ...old };
            for (const fractionColumn of fractionColumns) {
              newColumnSizing[fractionColumn.id] = table._getHeaderSize(
                fractionColumn.id,
              ).width;
            }
            return newColumnSizing;
          });
        }

        table.setColumnSizingInfo((old) => ({
          ...old,
          startOffset: clientX,
          startSize,
          deltaOffset: 0,
          deltaPercentage: 0,
          columnSizingStart,
          isResizingColumn: column.id,
        }));
      };
    };

    header.getResizeExpandHandler = () => {
      const column = table.getColumn(header.column.id);
      const canResize = column?.getCanResize();

      return (event: ReactMouseEvent<HTMLButtonElement>) => {
        if (event.type !== 'dblclick') {
          return;
        }

        if (!column || !canResize) {
          return;
        }

        event.persist();

        const target = event.target;
        if (target instanceof HTMLElement === false) {
          return;
        }

        const container: HTMLElement | null = target.closest(
          '.strato-table-virtualization-container',
        );
        const startSize =
          target.previousElementSibling?.getBoundingClientRect().width;

        // If the header could not be measured, we can't resize it
        if (startSize === undefined) {
          return;
        }

        // Get the starting clientX where the drag started.
        const clientX = event.clientX;

        const columnSizingStart: [string, number][] = [[column.id, startSize]];
        // While dragging set the custom property value
        container?.style.setProperty('--strato-resizing-size', 'max-content');

        table.setColumnSizingInfo((old) => ({
          ...old,
          startOffset: clientX,
          startSize,
          deltaOffset: 0,
          deltaPercentage: 0,
          columnSizingStart,
          isResizingColumn: column.id,
        }));

        table._queue(() => {
          const target = event.target;
          if (target instanceof HTMLButtonElement) {
            const targetRow = target.dataset.headerRow;
            const targetColumn = target.dataset.column;
            const parent = target.parentElement;

            const cell = parent?.querySelector(
              `div[data-header-row="${CSS.escape(
                String(targetRow),
              )}"][data-column="${CSS.escape(String(targetColumn))}"]`,
            );

            if (cell) {
              const { width } = cell.getBoundingClientRect();
              table.setColumnSizingInfo((old) => ({
                ...old,
                isResizingColumn: false,
                startOffset: null,
                startSize: null,
                deltaOffset: null,
                deltaPercentage: null,
                columnSizingStart: [],
              }));
              table.setColumnSizing((old) => ({
                ...old,
                [column.id]: width,
              }));
            }
            // After all is updated and measured, remove the property again
            requestAnimationFrame(() => {
              container?.style.removeProperty('--strato-resizing-size');
            });
          }
        });
      };
    };

    header.getKeyboardResizeHandler = () => {
      const column = table.getColumn(header.column.id);
      const canResize = column?.getCanResize();

      return (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        const target = event.target;
        if (
          !column ||
          !canResize ||
          target instanceof HTMLElement === false ||
          (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
        ) {
          // clear the focus on resizer
          if (event.key === 'Escape') {
            clearResizerFocus();
          }
          return;
        }

        event.persist();

        const startSize =
          target.previousElementSibling?.getBoundingClientRect().width;

        // If the header could not be measured, we can't resize it
        if (startSize === undefined) {
          return;
        }

        let offset = 0;
        if (event.key === 'ArrowLeft') {
          offset = -10;
        } else if (event.key === 'ArrowRight') {
          offset = 10;
        }

        if (event.ctrlKey === true) {
          offset *= 10;
        }

        table.setColumnSizing((old) => ({
          ...old,
          [column.id]: startSize + offset,
        }));
      };
    };
  },

  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getCanResize = () => {
      // Parent columns are not resizable, only their children are.
      if (column.columns.length !== 0) {
        return false;
      }

      // Need to evaluate that one here, as it is not yet available in the
      // createColumn wrapper.
      const lastLeafColumn = table.getVisibleLeafColumns().at(-1);

      // Early check if columnResizing is enabled on the DataTableV2
      if (table.options.enableColumnResizing === true) {
        // Check on the individual column.
        if (column.columnDef.enableResizing === false) {
          return false;
        }
        // Check if it is the last column
        if (column.id === lastLeafColumn?.id) {
          return false;
        }

        return true;
      }

      return false;
    };
  },
};

/**
 * @internal
 */
export function useColumnSizing<TData>(
  props: DataTableV2ColumnSizingProps,
  options: TableOptions<TData>,
) {
  options.enableColumnResizing = false;

  const configResizable = useDataTableV2ConfigElement('resizable');

  const [columnSizing, setColumnSizing] = useControllableState({
    value: props.columnSizing,
    defaultValue: props.defaultColumnSizing ?? {},
    onChange: props.onColumnSizingChange,
  });

  if (configResizable === true || props.resizable === true) {
    options.enableColumnResizing = true;
    options.state = {
      ...options.state,
      columnSizing,
    };

    options.onColumnSizingChange = (updater) => {
      const newValue = functionalUpdate(updater, columnSizing);
      setColumnSizing(newValue);
    };
  }
}
